import axiosClient from './axios'

/**
 * Blot Analysis Export API
 * For CHU interoperability - PDF, JSON, CSV, FHIR exports
 */

export interface ExportOptions {
  patientId?: string
  patientName?: string
  organizationName?: string
  includeImages?: boolean
  /** See ExportContext.includeBlotIds. */
  includeBlotIds?: number[]
}

/**
 * Operator-validated context propagated to ALL export formats so JSON / CSV /
 * FHIR carry the same clinical truth as the PDF — verdicts, comments, manually
 * placed bands (drag/rename), lot numbers, etc.
 */
export interface ExportContext {
  operator?: { name?: string | null; timestamp?: string | null }
  verdicts?: Record<number, 'POSITIF' | 'NÉGATIF' | 'DOUTEUX'>
  caseComment?: string | null
  blotComments?: Record<number, string>
  placedBands?: Record<number, Array<{ name: string; yFraction: number }>>
  /** Per-blot band names identified (detected ∪ manual) — so exports list the
   *  detected bands, not only the manually-added ones. */
  detectedBands?: Record<number, string[]>
  /** Paired-comparison roles (CIP-WB mother/child, J0/J+N…), the reader's flips
   *  included. The server cannot derive them: the pairing is guessed from the
   *  strip row order in the gallery. */
  pairedRoles?: Record<number, 'mother' | 'child'>
  lots?: {
    lotNumber?: string | null
    transferLotNumber?: string | null
    realizationDate?: string | null
  }
  organizationName?: string | null
  patientId?: string | null
  patientName?: string | null
  /** Bordeaux/Dynablot multitest only: restricts the export to these blot ids
   *  (the gallery's active pathology filter) — a case mixing several
   *  self-tests shouldn't export an unfinished one alongside a reviewed one.
   *  Omitted/empty exports every blot, as before. */
  includeBlotIds?: number[]
}

export interface SimpleJSONExport {
  version: string
  exportedAt: string
  exportedBy: string
  caseId: string
  sampleId: string
  filename: string
  test: {
    pathologyId: string
    pathologyName: string
    manufacturer: string
    version: string | null
  }
  analysisDate: string
  lastUpdated: string
  processingTimeSeconds: number
  summary: {
    totalBlots: number
    totalBands: number
    positiveControls: number[]
  }
  roi: {
    x: number
    y: number
    width: number
    height: number
  }
  blots: Array<{
    id: number
    position: {
      x0: number
      x1: number
      yTop: number
      yBottom: number
    }
    numBands: number
    numPeaks: number
    peaks: number[]
    profile: number[]
  }>
  bandCalibration: Record<string, Record<string, { position: number; intensity: number }>> | null
  interpretationCriteria: any
  testPerformance: any
  clinicalLimitations: string[]
}

export interface PDFContent {
  header: {
    title: string
    subtitle: string
    logo: string | null
    date: string
  }
  identification: {
    caseId: string
    sampleId: string
    filename: string
    patientId: string | null
    patientName: string | null
    analysisDate: string
    organization: string
  }
  testInfo: {
    name: string
    manufacturer: string
    version: string | null
    antibodyClass: string | null
    description: string | null
  }
  summary: {
    totalBlots: number
    totalBands: number
    positiveControlCount: number
    processingTime: string
  }
  blotResults: Array<{
    id: number
    isPositiveControl: boolean
    numBands: number
    numPeaks: number
    position: string
    cropImage: string | null
    cropWidth?: number
    cropHeight?: number
    interpretation?: 'POSITIF' | 'NÉGATIF' | 'DOUTEUX' | 'CONTRÔLE' | 'À DÉTERMINER'
  }>
  interpretation: {
    positive: {
      criteria: string[]
      conclusion: string
    }
    negative: {
      criteria: string[]
      conclusion: string
    }
    equivocal: {
      criteria: string[]
      conclusion: string
    } | null
  } | null
  bandsReference: {
    specific: Array<{
      name: string
      molecularWeight: number
      required: boolean
      description: string
    }>
    nonSpecific: Array<{
      name: string
      molecularWeight: number
      description: string
    }>
    referenceZone: string
  } | null
  qualityControl: {
    positiveControl: string
    negativeControl: string
    notes: string[]
  } | null
  performance: {
    sensitivity: string
    sensitivityCI: string
    specificity: string
    specificityCI: string
  } | null
  limitations: string[]
  footer: {
    disclaimer: string
    generatedAt: string
    version: string
  }
}

// Export as JSON — POST so we can send the full ExportContext (verdicts,
// comments, placed bands, lots) in the request body.
export const exportAsJSON = (caseId: string, context?: ExportContext) =>
  axiosClient.post<SimpleJSONExport>(`blot-analysis/case/${caseId}/export/json`, context ?? {})

// Export as FHIR DiagnosticReport (for CHU/SIL interoperability)
export const exportAsFHIR = (caseId: string, context?: ExportContext) =>
  axiosClient.post(`blot-analysis/case/${caseId}/export/fhir`, context ?? {})

// Export as CSV — keeps a text response type for the raw CSV body.
export const exportAsCSV = async (caseId: string, context?: ExportContext): Promise<string> => {
  const response = await axiosClient.post(`blot-analysis/case/${caseId}/export/csv`, context ?? {}, {
    responseType: 'text',
  })
  return response.data as unknown as string
}

// Get PDF content data (for frontend PDF generation)
export const getPDFData = (caseId: string, options?: ExportOptions) => {
  const params = new URLSearchParams()
  if (options?.patientId) params.append('patientId', options.patientId)
  if (options?.patientName) params.append('patientName', options.patientName)
  if (options?.organizationName) params.append('organizationName', options.organizationName)
  if (options?.includeBlotIds && options.includeBlotIds.length > 0) {
    params.append('includeBlotIds', options.includeBlotIds.join(','))
  }
  if (options?.includeImages !== undefined) {
    params.append('includeImages', String(options.includeImages))
  }

  return axiosClient.get<{
    success: boolean
    pdfContent: PDFContent
  }>(`blot-analysis/case/${caseId}/export/pdf-data?${params.toString()}`)
}

// Get all export formats at once
export const exportAll = (caseId: string) =>
  axiosClient.get<{
    success: boolean
    caseId: string
    sampleId: string
    exports: {
      json: SimpleJSONExport
      fhir: any
      csv: string
      pdfContent: PDFContent
    }
  }>(`blot-analysis/case/${caseId}/export/all`)

// Helper function to download file
export const downloadFile = (content: string | Blob, filename: string, mimeType: string) => {
  const blob = content instanceof Blob ? content : new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// Download JSON export — context optional but recommended for clinical fidelity.
export const downloadJSON = async (caseId: string, sampleId?: string, context?: ExportContext) => {
  const response = await exportAsJSON(caseId, context)
  const filename = `blot-analysis-${sampleId || caseId}.json`
  downloadFile(JSON.stringify(response.data, null, 2), filename, 'application/json')
}

// Download FHIR export
export const downloadFHIR = async (caseId: string, sampleId?: string, context?: ExportContext) => {
  const response = await exportAsFHIR(caseId, context)
  const filename = `fhir-diagnostic-report-${sampleId || caseId}.json`
  downloadFile(JSON.stringify(response.data, null, 2), filename, 'application/fhir+json')
}

// Download CSV export
export const downloadCSV = async (caseId: string, sampleId?: string, context?: ExportContext) => {
  const csvContent = await exportAsCSV(caseId, context)
  const filename = `blot-analysis-${sampleId || caseId}.csv`
  downloadFile(csvContent, filename, 'text/csv;charset=utf-8')
}
